import {z} from 'zod'
import {EventSmall} from '../agenda/types'
import {AccessType} from '../access-types/types'
import {DateRangeFilterEnum, PatientAgeRange} from '@/types'
import {AssistiveRegimeEnum, ProjectStatus} from '../projects/types'
import dayjs, {formatDate} from '@/dayjs'

const PayOutcome = z.enum(['absence_20', 'absence_50', 'absence_nr', 'access_100'])

export const Absences = z.object({
    id: z.string(),
    projectId: z.string(),
    patientId: z.string(),
    consultantId: z.string().nullable(),
    payOutcome: PayOutcome,
    status: z.string()
})

export const AccessesColumn = z.enum([
    'date',
    'type',
    'sector',
    'assistiveRegime',
    'groupName',
    'accessType',
    'consultantId',
    'status',
    'report',
    'aslId',
    'rateCode',
    'remappedSiarAccessType',
    'access_100',
    'isRemote',
    'absence_20',
    'absence_50',
    'absence_nr',
    'communicatedOnTime',
    'projectCode',
    'projectStartDate',
    'projectExpirationDate',
    'projectStatus',
    'patientId',
    'moreActions',
    'select'
])
export type AccessesColumn = z.infer<typeof AccessesColumn>

export const AccessesType = z.enum(['absence', 'access'])
export type AccessesType = z.infer<typeof AccessesType>

export const ExportRiaError = z.object({
    code: z.string(),
    message: z.string()
})
export type ExportRiaError = z.infer<typeof ExportRiaError>

export const AccessRateCode = z.enum(['AA1', 'AA2', 'AM', 'D1', 'D2'])
export type AccessRateCode = z.infer<typeof AccessRateCode>

export const RemappedSiarAccessType = z.enum([
    'Valutazione e osservazione',
    'Riabilitazione neuromotoria',
    'Riabilitazione logopedica',
    'Riabilitazione psicomotoria',
    'Riabilitazione cognitiva-neuropsicologica',
    'Parent training',
    'Terapia occupazionale',
    'Intervento educativo',
    'Elaborazione',
    '<15min (non contabilizzato)',
    'Counseling',
    'Adempimenti Legge 104'
])
export type RemappedSiarAccessType = z.infer<typeof RemappedSiarAccessType>

export const Access = z.object({
    projectId: z.string().nullable(),
    id: z.string(),
    remappedSiarAccessType: RemappedSiarAccessType.nullish(),
    consultant: z
        .object({
            id: z.string(),
            specialization: z.string(),
            specializationId: z.string(),
            fullName: z.string()
        })
        .nullish(),
    type: AccessesType,
    event: EventSmall,
    payOutcome: PayOutcome,
    patientId: z.string(),
    specification: z.string().nullable(),
    consultantId: z.string().nullable(),
    rateCode: AccessRateCode.nullable(),
    status: z.enum(['validated', 'to_validate', 'excluded', 'accounted']),
    sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance']),
    project: z
        .object({
            sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance']),
            code: z.number(),
            startDate: z.string().min(1).nullable(),
            expirationDate: z.string().nullable(),
            status: ProjectStatus.nullable(),
            assistiveRegime: AssistiveRegimeEnum.nullish()
        })
        .nullable(),
    patient: z
        .object({
            serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
            residenceAslDistrict: z
                .object({
                    name: z.string().nullable(),
                    aslName: z.string().nullable()
                })
                .nullable()
        })
        .nullable(),
    medicalRecordReport: z
        .object({
            id: z.string(),
            signedAt: z.string().nullable(),
            status: z.enum(['draft', 'signed'])
        })
        .nullish()
})
export type Access = z.infer<typeof Access>

export const AccessesDetail = z.object({
    accessType: AccessType,
    numberOfAccesses: z.number().int()
})

export const ValidatorSearchParams = z.object({
    consultant: z.string().optional(),
    patient: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional()
})
export type ValidatorSearchParams = z.infer<typeof ValidatorSearchParams>

export const AccessesSearchParams = z.object({
    limit: z.coerce.number().int().positive().default(50),
    page: z.coerce.number().int().positive().default(1),
    search: z.string().trim().optional(),
    filters: z.string().optional(),
    view: z.string().min(1).default('all'),
    sorts: z.string().optional(),
    columnVisibility: z.string().optional(),
    startDate: z.string().default(formatDate(dayjs().startOf('month').toString(), 'YYYY-MM-DD')),
    endDate: z.string().default(formatDate(dayjs().endOf('month').toString(), 'YYYY-MM-DD')),
    range: DateRangeFilterEnum.default('month')
})
export type AccessesSearchParams = z.infer<typeof AccessesSearchParams>

export const ExportAccessesReportError = z.object({
    code: z.enum(['accesses-with-wrong-status']),
    message: z.string()
})
export type ExportAccessesReportError = z.infer<typeof ExportAccessesReportError>
